import { useState } from "react";
import AuthService from "../../services/auth.service";
import BusinessService from "../../services/business.service";
import { useSelectedBusiness } from "../../context/BusinessContext";
import { HiUser, HiBuildingOffice2, HiExclamationTriangle, HiPencil, HiArrowPath, HiCodeBracket, HiEye, HiEyeSlash, HiClipboard } from "react-icons/hi2";

function InfoRow({ label, value }) {
  return (
    <div className="flex items-start justify-between py-3 border-b border-gray-50 last:border-0">
      <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide w-36 flex-shrink-0 pt-0.5">
        {label}
      </span>
      <span className="text-sm text-gray-700 font-medium text-right">
        {value || "—"}
      </span>
    </div>
  );
}

function Section({ title, icon, children, action }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-bold text-gray-700 flex items-center gap-2">
          <span className="text-violet-500">{icon}</span>
          {title}
        </h2>
        {action}
      </div>
      {children}
    </div>
  );
}

function Field({ label, name, value, onChange, type = "text", options }) {
  const base =
    "w-full px-3 py-2 text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-400 text-gray-800";
  if (options) {
    return (
      <div>
        <label className="block text-xs font-semibold text-gray-500 mb-1">
          {label}
        </label>
        <select name={name} value={value ?? ""} onChange={onChange} className={base}>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
    );
  }
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-500 mb-1">
        {label}
      </label>
      <input
        type={type}
        name={name}
        value={value ?? ""}
        onChange={onChange}
        className={base}
      />
    </div>
  );
}

export default function SettingsPage() {
  const { selectedBusiness: business, setSelectedBusiness } = useSelectedBusiness();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const [form, setForm] = useState({});
  const [apiKey, setApiKey] = useState(null);
  const [apiKeyVisible, setApiKeyVisible] = useState(false);
  const [generatingKey, setGeneratingKey] = useState(false);
  const [keyCopied, setKeyCopied] = useState(false);

  const user = AuthService.getCurrentUser()?.user;

  function startEdit() {
    setForm({
      name: business?.name ?? "",
      email: business?.email ?? "",
      phone: business?.phone ?? "",
      address_line_1: business?.address_line_1 ?? "",
      address_line_2: business?.address_line_2 ?? "",
      city: business?.city ?? "",
      province: business?.province ?? "",
      postal_code: business?.postal_code ?? "",
      country: business?.country ?? "",
      website: business?.website ?? "",
      monthly_volume_target: business?.monthly_volume_target ?? "",
      billing_type: business?.billing_type ?? "order_to_order",
    });
    setEditing(true);
    setError("");
    setSuccess(false);
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  async function handleSave() {
    setSaving(true);
    setError("");
    try {
      const payload = { ...form };
      if (payload.monthly_volume_target !== "")
        payload.monthly_volume_target = Number(payload.monthly_volume_target);
      else delete payload.monthly_volume_target;

      const updated = await BusinessService.update(business.id, payload);
      if (setSelectedBusiness) setSelectedBusiness(updated);
      setSuccess(true);
      setEditing(false);
    } catch (err) {
      setError(err?.response?.data?.message ?? "Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="px-4 py-8 lg:px-8">
      <div className="mb-6">
        <h1 className="text-2xl font-extrabold text-gray-900">Settings</h1>
        <p className="text-sm text-gray-400 mt-0.5">
          Your business and account details
        </p>
      </div>

      {error && (
        <div className="mb-5 p-4 rounded-xl bg-red-50 border border-red-100 text-red-700 text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-5 p-4 rounded-xl bg-green-50 border border-green-100 text-green-700 text-sm font-semibold">
          Business profile updated successfully.
        </div>
      )}

      <div className="space-y-4">
        {/* Account */}
        <Section
          title="Account"
          icon={<HiUser className="h-4 w-4" />}
        >
          <div className="flex items-center gap-4 mb-4 pb-4 border-b border-gray-50">
            {user?.profile_pic_url ? (
              <img src={user.profile_pic_url} alt="" className="h-14 w-14 rounded-xl object-cover flex-shrink-0" />
            ) : (
              <div className="h-14 w-14 rounded-xl bg-violet-100 flex items-center justify-center flex-shrink-0">
                <span className="text-violet-600 text-xl font-bold">
                  {(user?.first_name?.[0] ?? "?").toUpperCase()}
                </span>
              </div>
            )}
            <div>
              <p className="font-bold text-gray-900">{user?.first_name} {user?.last_name}</p>
              <p className="text-sm text-gray-400">{user?.email}</p>
              <p className="text-sm text-gray-400">{user?.cellphone}</p>
            </div>
          </div>
          <InfoRow label="Role" value={user?.role?.replace(/_/g, " ")} />
          <InfoRow label="User ID" value={`#${user?.id}`} />
        </Section>

        {/* Business Profile */}
        {business && (
          <Section
            title="Business Profile"
            icon={<HiBuildingOffice2 className="h-4 w-4" />}
            action={
              !editing && (
                <button
                  onClick={startEdit}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-violet-200 text-xs font-semibold text-violet-600 hover:bg-violet-50 transition-colors"
                >
                  <HiPencil className="h-3.5 w-3.5" />
                  Edit
                </button>
              )
            }
          >
            {editing ? (
              <div className="space-y-4">
                {/* Basic Info */}
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wide">Basic Info</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Business Name" name="name" value={form.name} onChange={handleChange} />
                  <Field label="Email" name="email" value={form.email} onChange={handleChange} type="email" />
                  <Field label="Phone" name="phone" value={form.phone} onChange={handleChange} type="tel" />
                  <Field label="Website" name="website" value={form.website} onChange={handleChange} />
                </div>

                {/* Address */}
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wide pt-2">Address</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="sm:col-span-2">
                    <Field label="Address Line 1" name="address_line_1" value={form.address_line_1} onChange={handleChange} />
                  </div>
                  <div className="sm:col-span-2">
                    <Field label="Address Line 2" name="address_line_2" value={form.address_line_2} onChange={handleChange} />
                  </div>
                  <Field label="City" name="city" value={form.city} onChange={handleChange} />
                  <Field label="Province" name="province" value={form.province} onChange={handleChange} />
                  <Field label="Postal Code" name="postal_code" value={form.postal_code} onChange={handleChange} />
                  <Field label="Country" name="country" value={form.country} onChange={handleChange} />
                </div>

                {/* Business Settings */}
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wide pt-2">Business Settings</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field
                    label="Billing Type"
                    name="billing_type"
                    value={form.billing_type}
                    onChange={handleChange}
                    options={[
                      { value: "order_to_order", label: "Order to Order" },
                      { value: "monthly_invoicing", label: "Monthly Invoicing" },
                      { value: "prepaid", label: "Prepaid Wallet" },
                    ]}
                  />
                  <Field
                    label="Monthly Volume Target (parcels)"
                    name="monthly_volume_target"
                    value={form.monthly_volume_target}
                    onChange={handleChange}
                    type="number"
                  />
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end gap-3 pt-2">
                  <button
                    onClick={() => setEditing(false)}
                    disabled={saving}
                    className="px-4 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="inline-flex items-center gap-2 px-5 py-2 rounded-xl bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white text-sm font-semibold transition-colors"
                  >
                    {saving ? (
                      <>
                        <HiArrowPath className="h-4 w-4 animate-spin" />
                        Saving…
                      </>
                    ) : (
                      "Save Changes"
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <InfoRow label="Name" value={business.name} />
                <InfoRow label="Email" value={business.email} />
                <InfoRow label="Phone" value={business.phone} />
                <InfoRow label="Website" value={business.website} />
                <InfoRow
                  label="Address"
                  value={[business.address_line_1, business.address_line_2, business.city, business.province, business.postal_code, business.country].filter(Boolean).join(", ")}
                />
                <InfoRow label="Billing Type" value={business.billing_type?.replace(/_/g, " ")} />
                <InfoRow
                  label="Monthly Target"
                  value={business.monthly_volume_target != null ? `${business.monthly_volume_target} parcels/mo` : null}
                />
                <InfoRow label="Account Tier" value={business.account_tier} />
              </>
            )}
          </Section>
        )}

        {/* API Integration */}
        {business && (
          <Section
            title="API Integration"
            icon={<HiCodeBracket className="h-4 w-4" />}
          >
            <p className="text-sm text-gray-500 mb-4">
              Use your API key to integrate your systems with Parcelin.{" "}
              <a href="/developers" className="text-violet-600 underline">View API docs</a>
            </p>

            {apiKey ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 font-mono text-sm text-gray-700 select-all">
                    {apiKeyVisible ? apiKey : "biz_" + "•".repeat(32)}
                  </div>
                  <button
                    onClick={() => setApiKeyVisible((v) => !v)}
                    className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"
                    title={apiKeyVisible ? "Hide" : "Reveal"}
                  >
                    {apiKeyVisible ? <HiEyeSlash className="h-4 w-4" /> : <HiEye className="h-4 w-4" />}
                  </button>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(apiKey);
                      setKeyCopied(true);
                      setTimeout(() => setKeyCopied(false), 2000);
                    }}
                    className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"
                    title="Copy"
                  >
                    <HiClipboard className="h-4 w-4" />
                  </button>
                </div>
                {keyCopied && (
                  <p className="text-xs text-green-600 font-semibold">Copied to clipboard!</p>
                )}
                <p className="text-xs text-amber-600">
                  Save this key now — it won't be shown again. If you lose it, generate a new one.
                </p>
              </div>
            ) : (
              <button
                onClick={async () => {
                  setGeneratingKey(true);
                  setError("");
                  try {
                    const result = await BusinessService.regenerateApiKey(business.id);
                    setApiKey(result.api_key);
                    setApiKeyVisible(true);
                  } catch (err) {
                    setError(err?.response?.data?.message ?? "Failed to generate API key.");
                  } finally {
                    setGeneratingKey(false);
                  }
                }}
                disabled={generatingKey}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white text-sm font-semibold transition-colors"
              >
                {generatingKey ? (
                  <>
                    <HiArrowPath className="h-4 w-4 animate-spin" />
                    Generating…
                  </>
                ) : (
                  "Generate API Key"
                )}
              </button>
            )}
          </Section>
        )}

        {/* Account Actions */}
        <Section
          title="Account Actions"
          icon={<HiExclamationTriangle className="h-4 w-4" />}
        >
          <div className="flex flex-wrap gap-3">
            <a
              href="/contact"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
            >
              Contact Support
            </a>
            <a
              href="/profile"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-violet-200 text-sm font-semibold text-violet-600 hover:bg-violet-50 transition-colors"
            >
              Edit Profile
            </a>
          </div>
        </Section>
      </div>
    </div>
  );
}
